import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import './Particles.css';

/**
 * Default number of particles to render.
 * Kept moderate for performance while maintaining visual density.
 */
const DEFAULT_PARTICLE_COUNT = 40;

/**
 * Ambient floating particles that create an arctic atmosphere.
 *
 * Generates randomized ice dust / snowflake particles that drift slowly
 * across the viewport. Each particle has unique size, position, speed,
 * and opacity to create organic, non-uniform movement.
 *
 * Uses CSS custom properties for randomization to avoid re-renders
 * and keep animations GPU-accelerated via transform/opacity only.
 *
 * @param {Object} props
 * @param {number} [props.count=40] - Number of particles to render
 * @returns {JSX.Element}
 */
function Particles({ count = DEFAULT_PARTICLE_COUNT }) {
  /**
   * Generate particle configurations once on mount.
   * Randomizes visual properties to create organic variation.
   */
  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        size: 2 + Math.random() * 3,
        left: Math.random() * 100,
        startY: Math.random() * 100,
        delay: Math.random() * 20,
        duration: 15 + Math.random() * 20,
        opacity: 0.15 + Math.random() * 0.25,
        sway: 20 + Math.random() * 40,
      })),
    [count]
  );

  return (
    <div className="particles" aria-hidden="true">
      {particles.map((particle) => (
        <span
          key={particle.id}
          className="particles__dot"
          style={{
            '--size': `${particle.size}px`,
            '--left': `${particle.left}%`,
            '--start-y': `${particle.startY}%`,
            '--delay': `${particle.delay}s`,
            '--duration': `${particle.duration}s`,
            '--opacity': particle.opacity,
            '--sway': `${particle.sway}px`,
          }}
        />
      ))}
    </div>
  );
}

Particles.propTypes = {
  count: PropTypes.number,
};

export default Particles;
